import { Maybe } from '@crocodille/common';
import cn from 'classnames';
import Link from 'next/link';
import type { ParsedUrlQuery } from 'querystring';
import React from 'react';
import { useTranslation } from 'react-i18next';
import * as routing from '../../../routing';
import Image from '../../elements/image/image';
import styles from './coupon-list.module.scss';

const CouponListElement: React.FC<
  React.PropsWithChildren<{
    readonly tagCounts: ReadonlyMap<string, number>;
    readonly allTags: readonly string[];
    readonly allCouponCount: number;
    readonly tagFilter: ReadonlySet<string>;
    toggleTagFilter: (tag: string) => void;
    resetTagFilter: () => void;
  }>
> = ({
  children,
  tagCounts,
  allTags,
  allCouponCount,
  tagFilter,
  toggleTagFilter,
  resetTagFilter,
}) => {
  const { t } = useTranslation();

  return (
    <div className={styles.container}>
      <div className={styles.headerContainer}>
        <h1 className={styles.header}>{t('coupons.page_title')}</h1>
        <ul className={styles.tagFilter}>
          <li className={styles.tagFilterItem}>
            <button
              className={cn(styles.tagFilterButton, {
                [styles.isActive]: tagFilter.size === 0,
              })}
              onClick={() => resetTagFilter()}
            >
              {t('coupons.tag_all')} ({allCouponCount})
            </button>
          </li>
          {allTags.map((tag) => (
            <li key={tag} className={styles.tagFilterItem}>
              <button
                className={cn(styles.tagFilterButton, {
                  [styles.isActive]: tagFilter.has(tag),
                })}
                onClick={() => toggleTagFilter(tag)}
              >
                {tag} ({tagCounts.get(tag) ?? 0})
              </button>
            </li>
          ))}
        </ul>
      </div>
      <ul className={styles.list}>{children}</ul>
      <p className={styles.footer}>{t('coupons.thats_all')}</p>
      <p className={styles.disclaimer}>{t('coupons.location_disclaimer')}</p>
    </div>
  );
};

export interface CouponItemProps {
  readonly id: number;
  readonly imagePath: string | null;
  readonly name: string;
  readonly description: string;
  readonly discountText: string;
  readonly tags: Maybe<readonly string[]>;
  readonly query?: ParsedUrlQuery;
  readonly onClick?: () => void;
}

const Item: React.FC<React.PropsWithChildren<CouponItemProps>> = ({
  id,
  imagePath,
  name,
  discountText,
  tags,
  query,
  onClick,
}) => {
  return (
    <li className={styles.item}>
      <Link
        href={{
          pathname: routing.couponDetail({ id }),
          query: { _headless: 'true', ...query },
        }}
        prefetch={false}
        className={styles.itemInner}
        onClick={
          onClick
            ? (e) => {
                e.preventDefault();
                onClick();
              }
            : undefined
        }
      >
        <div className={styles.imageContainer}>
          <Image
            src={imagePath}
            alt=""
            role="presentation"
            className={styles.image}
            layout="fill"
            objectFit="cover"
            sizes="50vw"
          />
          {tags?.length ? (
            <ul className={styles.tags}>
              {tags.map((tag) => (
                <li key={tag} className={styles.tag}>
                  {tag}
                </li>
              ))}
            </ul>
          ) : null}
        </div>
        <div className={styles.labelsOverlay}>
          <h2 className={styles.name}>{name}</h2>
          <p className={styles.discount}>
            {discountText === '' ? <>&nbsp;</> : discountText}
          </p>
        </div>
      </Link>
    </li>
  );
};

const CouponList = Object.assign(CouponListElement, { Item });

export default CouponList;
